import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Fab,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';


const themeStyles = {
  colors: {
    background: "#F9FAFB",
    surface: "#FFFFFF",
    primary: "#012442",
    primarySoft: "rgba(1, 36, 66, 0.06)",
    primaryBorder: "rgba(1, 36, 66, 0.12)",
    primaryBorderStrong: "rgba(1, 36, 66, 0.18)",
    textPrimary: "#1E2124",
    textSecondary: "rgba(30, 33, 36, 0.72)",
    highlight: "#FF3100",
    highlightSoft: "rgba(255, 49, 0, 0.08)",
    white: "#FFFFFF",
  },
  shadow:
    "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
  radius: {
    sm: "12px",
    md: "16px",
    lg: "24px",
    xl: "28px",
    pill: "999px",
  },
};

export default function InvoiceList({ items = [], onConfirm }) {
  const [openXml, setOpenXml] = useState(false);

  const normalizedItems = Array.isArray(items) ? items : [];
  const hasMoreThanTwo = normalizedItems.length > 2;
  const previewItems = hasMoreThanTwo
    ? normalizedItems.slice(0, 2)
    : normalizedItems;

  const handleOpen = () => setOpenXml(true);
  const handleClose = () => setOpenXml(false);

  const confirmImport = () => {
    if (typeof onConfirm === "function") {
      onConfirm(normalizedItems);
      return;
    }
    console.log("Importar XML");
  };

  const resolveItemLabel = (item, index) => {
    if (item == null) return `Item ${index + 1}`;
    if (typeof item === "string" || typeof item === "number") {
      return String(item);
    }

    return (
      item.label ||
      item.name ||
      item.title ||
      item.value ||
      item.key ||
      item.numero ||
      item.numeroNfe ||
      item.id ||
      `Item ${index + 1}`
    );
  };

  const resolveItemUrl = (item) => {
    if (!item || typeof item !== "object") return "";
    const url = item.url || item.link || item.href;
    return url ? String(url) : "";
  };

  const getItemKey = (item, index) => {
    if (item && typeof item === "object") {
      return item.id || item.key || item.value || item.label || `item-${index}`;
    }
    return `item-${String(item)}-${index}`;
  };

  const renderItem = (item, index, expanded = false) => {
    const label = resolveItemLabel(item, index);
    const url = resolveItemUrl(item);

    return (
      <Box
        key={getItemKey(item, index)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minHeight: expanded ? 58 : 48,
          px: expanded ? 2 : 1.5,
          py: expanded ? 1.3 : 1,
          borderRadius: themeStyles.radius.md,
          backgroundColor: themeStyles.colors.surface,
          border: `1px solid ${themeStyles.colors.primaryBorder}`,
          boxShadow: themeStyles.shadow,
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: themeStyles.colors.primaryBorderStrong,
            transform: "translateY(-1px)",
          },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            minWidth: 34,
            borderRadius: "10px",
            backgroundColor: themeStyles.colors.primarySoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DescriptionOutlinedIcon
            sx={{
              fontSize: 18,
              color: themeStyles.colors.primary,
            }}
          />
        </Box>

        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            style={{
              color: themeStyles.colors.textPrimary,
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.5,
              width: "100%",
              wordBreak: "break-word",
            }}
          >
            {label}
          </a>
        ) : (
          <Typography
            sx={{
              color: themeStyles.colors.textPrimary,
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {label}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      {normalizedItems.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            overflowX: "auto",
            py: 0.5,
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(1, 36, 66, 0.16)",
              borderRadius: themeStyles.radius.pill,
            },
          }}
        >
          {previewItems.map((item, index) => renderItem(item, index))}

          {hasMoreThanTwo && (
            <Button
              onClick={handleOpen}
              size="small"
              sx={{
                flexShrink: 0,
                // textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                borderRadius: themeStyles.radius.sm,
                px: 2,
                py: 1,
                backgroundColor: themeStyles.colors.primary,
                color: themeStyles.colors.surface,
                border: `1px solid ${themeStyles.colors.primaryBorderStrong}`,
                boxShadow: themeStyles.shadow,
                "&:hover": {
                  backgroundColor: themeStyles.colors.highlightSoft,
                  color: themeStyles.colors.highlight,
                  borderColor: themeStyles.colors.highlight,
                },
              }}
            >

              <RemoveRedEyeIcon sx={{ mr: 1, color: themeStyles.colors.highlight }} />

              Ver todas as NF-s
            </Button>
          )}
        </Box>
      )}

      <Dialog
        open={openXml}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: themeStyles.radius.xl,
            backgroundColor: themeStyles.colors.background,
            border: `1px solid ${themeStyles.colors.primaryBorder}`,
            boxShadow: themeStyles.shadow,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            background:
              "linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)",
            borderBottom: `1px solid ${themeStyles.colors.primaryBorder}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: themeStyles.radius.md,
                backgroundColor: themeStyles.colors.primarySoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: themeStyles.shadow,
              }}
            >
              <ListAltIcon
                sx={{
                  fontSize: 28,
                  color: themeStyles.colors.primary,
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: themeStyles.colors.primary,
                  lineHeight: 1.2,
                }}
              >
                Lista de notas importadas
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "14px",
                  fontWeight: 400,
                  color: themeStyles.colors.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                Revise todas as notas importadas pelo motorista.
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <Chip
                  label={`${normalizedItems.length} ${normalizedItems.length !== 1 ? "Notas" : "Nota"
                    } encontrada${normalizedItems.length !== 1 ? "s" : ""
                    }`}
                  sx={{
                    height: 30,
                    borderRadius: themeStyles.radius.pill,
                    backgroundColor: themeStyles.colors.primarySoft,
                    color: themeStyles.colors.primary,
                    fontWeight: 600,
                    border: `1px solid ${themeStyles.colors.primaryBorder}`,
                  }}
                />
              </Box>
            </Box>

            <Fab
              aria-label="fechar"
              size="small"
              onClick={handleClose}
              sx={{
                width: 38,
                height: 38,
                minHeight: 38,
                backgroundColor: themeStyles.colors.highlight,
                color: themeStyles.colors.white,
                boxShadow: themeStyles.shadow,
                "&:hover": {
                  backgroundColor: themeStyles.colors.highlight,
                },
              }}
            >
              <HighlightOffIcon sx={{ fontSize: 20 }} />
            </Fab>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            py: 3,
            backgroundColor: themeStyles.colors.background,
          }}
        >
          {/* <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: themeStyles.radius.lg,
              backgroundColor: themeStyles.colors.surface,
              border: `1px solid ${themeStyles.colors.primaryBorder}`,
              boxShadow: themeStyles.shadow,
            }}
          >
            <DialogContentText
              sx={{
                m: 0,
                color: themeStyles.colors.textPrimary,
                fontSize: "15px",
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: themeStyles.colors.highlight,
                  fontWeight: 600,
                  mr: 0.5,
                }}
              >
                Atenção:
              </Box>
              selecione o XML do MDF-e para importar os dados corretamente.
            </DialogContentText>
          </Box> */}

          <Divider sx={{ mb: 2, borderColor: themeStyles.colors.primaryBorder }} />

          <Stack spacing={1.5}>
            {normalizedItems.length > 0 ? (
              normalizedItems.map((item, index) => renderItem(item, index, true))
            ) : (
              <Box
                sx={{
                  p: 4,
                  borderRadius: themeStyles.radius.lg,
                  backgroundColor: themeStyles.colors.surface,
                  border: `1px dashed ${themeStyles.colors.primaryBorderStrong}`,
                  textAlign: "center",
                  boxShadow: themeStyles.shadow,
                }}
              >
                <Typography
                  sx={{
                    color: themeStyles.colors.textSecondary,
                    fontSize: "14px",
                    fontWeight: 400,
                  }}
                >
                  Nenhum item para exibir.
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.25,
            backgroundColor: themeStyles.colors.surface,
            borderTop: `1px solid ${themeStyles.colors.primaryBorder}`,
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              color: themeStyles.colors.textSecondary,
              fontWeight: 400,
            }}
          >
            Todas as notas importadas pelo motorista.
          </Typography>

          {/* <Button
            onClick={confirmImport}
            sx={{
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 600,
              borderRadius: themeStyles.radius.pill,
              px: 3,
              py: 1.2,
              backgroundColor: themeStyles.colors.primary,
              color: themeStyles.colors.white,
              boxShadow: themeStyles.shadow,
              "&:hover": {
                backgroundColor: themeStyles.colors.primary,
                boxShadow: themeStyles.shadow,
              },
            }}
          >
            Selecionar XML
          </Button> */}
        </DialogActions>
      </Dialog>
    </>
  );
}